(function () {
  'use strict';

  const gsap = window.gsap;
  const { subEvents, PUB_SUB_EVENTS, animProp, easePower4, easeExpo } = window.Universe;

  class MobileMenu {
    constructor(toggles) {
      this.init(toggles);
    }

    setup() {
      this.ctx.add(() => {
        const bind = (els, handler) => { els.forEach((el) => { el.addEventListener('click', handler); }); };
        const unbind = (els, handler) => { els.forEach((el) => { el.removeEventListener('click', handler); }); };

        bind(this.DOM.toggles, this._onMenuToggle);
        bind(this.DOM.subToggles, this._onMenuSubClick);
        bind(this.DOM.subClosers, this._onMenuSubClose);
        bind(this.DOM.subsubToggles, this._onMenuSubSubClick);
        bind(this.DOM.subsubClosers, this._onMenuSubSubClose);
        bind(this.DOM.overlays, this._onMenuToggle);
        bind(this.DOM.shopSublistToggles, this._onShopSublistToggle);

        return () => {
          unbind(this.DOM.toggles, this._onMenuToggle);
          unbind(this.DOM.subToggles, this._onMenuSubClick);
          unbind(this.DOM.subClosers, this._onMenuSubClose);
          unbind(this.DOM.subsubToggles, this._onMenuSubSubClick);
          unbind(this.DOM.subsubClosers, this._onMenuSubSubClose);
          unbind(this.DOM.overlays, this._onMenuToggle);
          unbind(this.DOM.shopSublistToggles, this._onShopSublistToggle);
        };
      });
    }

    openMenu() {
      if (!this.isAnimatingMenu) {
        this.isAnimatingMenu = true;

        this.previouslyFocused = document.activeElement;

        document.body.classList.add('is-mobile-menu-open');
        this.DOM.menuWrap.removeAttribute('inert');
        this.DOM.menuWrap.setAttribute('aria-hidden', 'false');

        this.DOM.toggles.forEach((toggle) => { toggle.setAttribute('aria-expanded', 'true'); });

        const headerMainMobile = document.querySelector('.header-nav__main--mobile');
        if (headerMainMobile) headerMainMobile.setAttribute('is-solid', true);

        document.addEventListener('keydown', this._onKeydown);

        subEvents.publish(PUB_SUB_EVENTS.mobileMenuOpen);

        this.ctx.add(() => {
          const openTl = gsap.timeline({
            onComplete: () => {
              this.isAnimatingMenu = false;
              this.isOpenMenu = true;
              const firstFocusable = this.getFocusableElements()[0];
              if (firstFocusable) firstFocusable.focus();
            },
          });

          openTl
            .set(this.DOM.menuWrap, { pointerEvents: 'all', opacity: 1, zIndex: 49, contentVisibility: 'visible' })
            .to(this.DOM.menuMain, {
              clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)',
              pointerEvents: 'all',
              duration: 1,
              ease: easeExpo,
            });
        });
      }
    }

    closeMenu(immediate = false) {
      if (!this.isAnimatingMenu) {
        this.isAnimatingMenu = true;

        document.body.classList.remove('is-mobile-menu-open');

        this.DOM.menuWrap.setAttribute('inert', '');
        this.DOM.menuWrap.setAttribute('aria-hidden', 'true');

        this.DOM.toggles.forEach((toggle) => { toggle.setAttribute('aria-expanded', 'false'); });

        const headerMainMobile = document.querySelector('.header-nav__main--mobile');
        if (headerMainMobile) headerMainMobile.removeAttribute('is-solid');

        document.removeEventListener('keydown', this._onKeydown);

        if (this.activeMenuSubId) {
          const relativeSub = this.DOM.menuWrap.querySelector(
            '.js-header-mobile-menu-submenu[data-sub-id="' + this.activeMenuSubId + '"]'
          );
          this.closeSub(relativeSub);
        }

        const focusTarget = (this.previouslyFocused && document.contains(this.previouslyFocused))
          ? this.previouslyFocused
          : this.DOM.toggles[0];

        this.ctx.add(() => {
          const closeTl = gsap.timeline({
            onComplete: () => {
              this.isAnimatingMenu = false;
              this.isOpenMenu = false;
              if (focusTarget && typeof focusTarget.focus === 'function') {
                focusTarget.focus();
              }
              this.previouslyFocused = null;
            },
          });

          closeTl
            .fromTo(
              this.DOM.menuMain,
              {
                clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)',
              },
              {
                clipPath: 'polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)',
                pointerEvents: 'none',
                duration: animProp(1, immediate),
                ease: easeExpo,
              }
            )
            .set(this.DOM.menuWrap, { pointerEvents: 'none', opacity: 0, zIndex: -1, contentVisibility: 'hidden' });
        });
      }
    }

    getFocusableElements() {
      const selector = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
      return Array.from(this.DOM.menuWrap.querySelectorAll(selector))
        .filter((el) => !el.closest('[inert]') && el.offsetParent !== null);
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        if (this.isAnimatingMenu && !this.isOpenMenu) {
          gsap.killTweensOf(this.DOM.menuMain);
          this.isAnimatingMenu = false;
          this.isOpenMenu = true;
        }
        this.closeMenu();
        return;
      }

      if (!this.isOpenMenu) return;
      if (event.key !== 'Tab') return;

      const focusables = this.getFocusableElements();
      if (focusables.length === 0) {
        event.preventDefault();
        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || !this.DOM.menuWrap.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !this.DOM.menuWrap.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    }

    openSub(subEl) {
      this.activeMenuSubId = subEl.dataset.subId;
      subEl.removeAttribute('inert');
      subEl.setAttribute('aria-hidden', 'false');

      this.ctx.add(() => {
        gsap.to(subEl, {
          x: 0,
          opacity: 1,
          xPercent: 0,
          pointerEvents: 'all',
          duration: 0.5,
          ease: easePower4,
        });
      });
    }

    closeSub(subEl) {
      this.activeMenuSubId = null;
      subEl.setAttribute('inert', '');
      subEl.setAttribute('aria-hidden', 'true');

      if (subEl.dataset.subId) {
        const activator = this.DOM.menuWrap.querySelector(
          '.js-header-mobile-menu-has-sub[data-sub-id="' + subEl.dataset.subId + '"]'
        );
        if (activator) activator.setAttribute('aria-expanded', 'false');
      }

      this.ctx.add(() => {
        gsap.to(subEl, {
          x: 0,
          opacity: 0,
          xPercent: 100,
          pointerEvents: 'none',
          duration: 0.8,
          ease: easeExpo,
        });
      });
    }

    openSubsub(subEl) {
      this.activeMenuSubSubId = subEl.dataset.subId;
      subEl.removeAttribute('inert');
      subEl.setAttribute('aria-hidden', 'false');

      this.ctx.add(() => {
        gsap.to(subEl, {
          x: 0,
          opacity: 1,
          xPercent: 0,
          pointerEvents: 'all',
          duration: 0.5,
          ease: easePower4,
        });
      });
    }

    closeSubsub(subEl) {
      this.activeMenuSubSubId = null;
      subEl.setAttribute('inert', '');
      subEl.setAttribute('aria-hidden', 'true');

      if (subEl.dataset.subId) {
        const activator = this.DOM.menuWrap.querySelector(
          '.js-header-mobile-menu-has-subsub[data-sub-id="' + subEl.dataset.subId + '"]'
        );
        if (activator) activator.setAttribute('aria-expanded', 'false');
      }

      this.ctx.add(() => {
        gsap.to(subEl, {
          x: 0,
          opacity: 0,
          xPercent: 100,
          pointerEvents: 'none',
          duration: 0.8,
          ease: easeExpo,
        });
      });
    }

    destroy() {
      document.body.classList.remove('is-mobile-menu-open');

      if (this._onKeydown) {
        document.removeEventListener('keydown', this._onKeydown);
      }

      if (this.ctx) {
        this.ctx.revert();
        this.ctx = null;
      }

      this.DOM = undefined;
    }

    init(toggles) {
      if (toggles) {
        this.DOM = {};
        this.DOM.toggles = toggles;
        this.DOM.menuWrap = document.querySelector('.js-header-mobile-menu');

        if (!this.DOM.menuWrap) {
          this.DOM = undefined;
          return;
        }

        this.DOM.menuMain = this.DOM.menuWrap.querySelector('.js-header-mobile-menu-main');
        this.DOM.subToggles = this.DOM.menuWrap.querySelectorAll('.js-header-mobile-menu-has-sub');
        this.DOM.subsubToggles = this.DOM.menuWrap.querySelectorAll('.js-header-mobile-menu-has-subsub');
        this.DOM.subClosers = this.DOM.menuWrap.querySelectorAll('.js-header-mobile-menu-submenu-close');
        this.DOM.subsubClosers = this.DOM.menuWrap.querySelectorAll('.js-header-mobile-menu-subsubmenu-close');
        this.DOM.subElements = this.DOM.menuWrap.querySelectorAll('.js-header-mobile-menu-submenu');
        this.DOM.subsubElements = this.DOM.menuWrap.querySelectorAll('.js-header-mobile-menu-subsubmenu');
        this.DOM.overlays = this.DOM.menuWrap.querySelectorAll('.js-header-mobile-menu__overlay');
        this.DOM.shopSublistToggles = this.DOM.menuWrap.querySelectorAll('.js-header-mobile-shop-toggle');
        this.DOM.shopSublists = this.DOM.menuWrap.querySelectorAll('.js-header-mobile-shop-sublist');

        this.ctx = gsap.context(() => {}, this.DOM.menuWrap);

        this.isAnimatingMenu = false;
        this.isOpenMenu = false;
        this.activeMenuSubId = null;
        this.activeMenuSubSubId = null;
        this.previouslyFocused = null;

        this._onKeydown = (event) => { this.onKeydown(event); };

        this._onMenuToggle = () => {
          return this.isOpenMenu ? this.closeMenu() : this.openMenu();
        };

        this._onMenuSubClick = (e) => {
          const activator = e.currentTarget;
          const relativeSubId = activator.dataset.subId;
          const newRelativeSubEl = this.DOM.menuWrap.querySelector(
            '.js-header-mobile-menu-submenu[data-sub-id="' + relativeSubId + '"]'
          );

          if (!this.activeMenuSubId) {
            activator.setAttribute('aria-expanded', 'true');
            this.openSub(newRelativeSubEl);
          } else if (this.activeMenuSubId) {
            const oldSubEl = this.DOM.menuWrap.querySelector(
              '.js-header-mobile-menu-submenu[data-sub-id="' + this.activeMenuSubId + '"]'
            );

            if (this.activeMenuSubId !== relativeSubId) {
              this.closeSub(oldSubEl);
              activator.setAttribute('aria-expanded', 'true');
              this.openSub(newRelativeSubEl);
            } else {
              this.closeSub(oldSubEl);
            }
          }
        };

        this._onMenuSubClose = () => {
          if (this.activeMenuSubId) {
            const oldSubEl = this.DOM.menuWrap.querySelector(
              '.js-header-mobile-menu-submenu[data-sub-id="' + this.activeMenuSubId + '"]'
            );
            this.closeSub(oldSubEl);
          }
        };

        this._onMenuSubSubClick = (e) => {
          const activator = e.currentTarget;
          const relativeSubId = activator.dataset.subId;
          const newRelativeSubEl = this.DOM.menuWrap.querySelector(
            '.js-header-mobile-menu-subsubmenu[data-sub-id="' + relativeSubId + '"]'
          );

          if (!this.activeMenuSubSubId) {
            activator.setAttribute('aria-expanded', 'true');
            this.openSubsub(newRelativeSubEl);
          } else if (this.activeMenuSubSubId) {
            const oldSubEl = this.DOM.menuWrap.querySelector(
              '.js-header-mobile-menu-subsubmenu[data-sub-id="' + this.activeMenuSubSubId + '"]'
            );

            if (this.activeMenuSubSubId !== relativeSubId) {
              this.closeSubsub(oldSubEl);
              activator.setAttribute('aria-expanded', 'true');
              this.openSubsub(newRelativeSubEl);
            } else {
              this.closeSubsub(oldSubEl);
            }
          }
        };

        this._onMenuSubSubClose = () => {
          if (this.activeMenuSubSubId) {
            const oldSubEl = this.DOM.menuWrap.querySelector(
              '.js-header-mobile-menu-subsubmenu[data-sub-id="' + this.activeMenuSubSubId + '"]'
            );
            this.closeSubsub(oldSubEl);
          }
        };

        this._onShopSublistToggle = (e) => {
          const activator = e.currentTarget;
          const sublistId = activator.dataset.sublistId;
          const sublist = this.DOM.menuWrap.querySelector('.js-header-mobile-shop-sublist[data-sublist-id="' + sublistId + '"]');

          if (!sublist) return;

          const isOpen = activator.getAttribute('aria-expanded') === 'true';

          if (isOpen) {
            activator.setAttribute('aria-expanded', 'false');
            sublist.setAttribute('aria-hidden', 'true');
            this.ctx.add(() => {
              gsap.to(sublist, { height: 0, autoAlpha: 0, duration: 0.4, ease: easePower4 });
            });
          } else {
            const parentGroup = activator.closest('.header-mobile-menu__submenu-shop-group');
            if (parentGroup) {
              const openToggles = parentGroup.querySelectorAll('.js-header-mobile-shop-toggle[aria-expanded="true"]');
              openToggles.forEach((toggle) => {
                if (toggle !== activator) {
                  const otherId = toggle.dataset.sublistId;
                  const otherSublist = this.DOM.menuWrap.querySelector(
                    '.js-header-mobile-shop-sublist[data-sublist-id="' + otherId + '"]'
                  );
                  toggle.setAttribute('aria-expanded', 'false');
                  if (otherSublist) {
                    otherSublist.setAttribute('aria-hidden', 'true');
                    this.ctx.add(() => {
                      gsap.to(otherSublist, { height: 0, autoAlpha: 0, duration: 0.4, ease: easePower4 });
                    });
                  }
                }
              });
            }

            activator.setAttribute('aria-expanded', 'true');
            sublist.setAttribute('aria-hidden', 'false');
            this.ctx.add(() => {
              gsap.to(sublist, { height: 'auto', autoAlpha: 1, duration: 0.4, ease: easePower4 });
            });
          }
        };

        this.setup();
      }
    }
  }

  window.Universe.MobileMenu = MobileMenu;
})();
